import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import type { SearchSchemaInput } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Eye, EyeOff } from "lucide-react";

import { supabase } from "@/integrations/supabase/client";
import { claimFirstAdmin, hasAnyAdmin, getCurrentAdminStatus } from "@/lib/auth-status.functions";
import { getPendingTotpFactorId, verifyTotpCode } from "@/lib/mfa-helpers";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/hooks/use-auth";
import { PasswordResetDialog } from "@/components/auth/PasswordResetDialog";

function AdminLoginPage() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const { user, refreshAdminStatus, signOut } = useAuth();
  const hasAnyAdminFn = useServerFn(hasAnyAdmin);
  const claimFirstAdminFn = useServerFn(claimFirstAdmin);
  const getAdminStatusFn = useServerFn(getCurrentAdminStatus);
  const { data: adminStatus } = useQuery({
    queryKey: ["has-any-admin"],
    queryFn: () => hasAnyAdminFn(),
    staleTime: 60_000,
  });
  const isFirstTime = adminStatus?.hasAdmin === false;


  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(
    search.error === "not_admin" ? "this account is not an admin. sign in with an admin account." : null
  );
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [mfaFactorId, setMfaFactorId] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);

  async function isSalesRep(): Promise<boolean> {
    const { data: u } = await supabase.auth.getUser();
    if (!u?.user?.id) return false;
    const { data } = await supabase.rpc("has_role", {
      _user_id: u.user.id,
      _role: "sales_rep",
    });
    return Boolean(data);
  }

  async function finishAdminSignIn() {
    const status = await getAdminStatusFn();
    if (!status.isAdmin) {
      if (await isSalesRep()) {
        navigate({ to: "/crm", replace: true });
        return;
      }
      await signOut();
      setError("this account is not an admin. sign in with an admin account.");
      return;
    }
    refreshAdminStatus();
    navigate({ to: "/admin" });
  }

  async function handleMfaSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!mfaFactorId) return;
    setError(null);
    if (!/^\d{6}$/.test(mfaCode.trim())) {
      setError("enter the 6-digit code from your authenticator app");
      return;
    }
    setBusy(true);
    try {
      await verifyTotpCode(mfaFactorId, mfaCode.trim());
      setMfaFactorId(null);
      setMfaCode("");
      await finishAdminSignIn();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "invalid code, try again");
    } finally {
      setBusy(false);
    }
  }

  async function handleMfaCancel() {
    await signOut();
    setMfaFactorId(null);
    setMfaCode("");
    setError(null);
  }

  useEffect(() => {
    let cancelled = false;
    if (!user || isFirstTime || busy || mfaFactorId) return;

    (async () => {
      try {
        await supabase.auth.getUser();
        // If the account has TOTP enrolled, require code before auto-navigating.
        const pendingFactor = await getPendingTotpFactorId();
        if (!cancelled && pendingFactor) {
          setMfaFactorId(pendingFactor);
          return;
        }
        const status = await getAdminStatusFn();
        if (!cancelled && status.isAdmin) {
          refreshAdminStatus();
          navigate({ to: "/admin", replace: true });
          return;
        }
        if (!cancelled && (await isSalesRep())) {
          navigate({ to: "/crm", replace: true });
        }
      } catch {
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.id, isFirstTime, busy, mfaFactorId, getAdminStatusFn, navigate, refreshAdminStatus]);

  async function handleClaimCurrentAccount() {
    setError(null);
    setInfo(null);
    setBusy(true);
    try {
      const result = await claimFirstAdminFn();
      if (result.alreadyAdmin || result.claimed) {
        refreshAdminStatus();
        navigate({ to: "/admin" });
        return;
      }
      setError("could not finish first admin setup");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "could not finish first admin setup");
    } finally {
      setBusy(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setBusy(true);
    try {
      if (isFirstTime) {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: { emailRedirectTo: window.location.origin + "/admin" },
        });
        if (error) {
          const normalized = error.message.toLowerCase();
          if (normalized.includes("already registered") || normalized.includes("user already registered")) {
            const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
            if (signInError) throw new Error("this email already exists. sign in with the correct password to claim first admin.");

            const result = await claimFirstAdminFn();
            if (result.alreadyAdmin || result.claimed) {
              refreshAdminStatus();
              navigate({ to: "/admin" });
              return;
            }
          }

          throw error;
        }

        if (data.user) {
          const result = await claimFirstAdminFn();
          if (result.alreadyAdmin || result.claimed) {
            refreshAdminStatus();
            navigate({ to: "/admin" });
            return;
          }
        }

        setInfo("admin account created. check your email to confirm, then sign in.");
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;

        // If the account has TOTP 2FA enrolled, require the code before proceeding.
        const pendingFactor = await getPendingTotpFactorId();
        if (pendingFactor) {
          setMfaFactorId(pendingFactor);
          setBusy(false);
          return;
        }

        await finishAdminSignIn();
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : isFirstTime ? "first admin setup failed" : "sign in failed");
    } finally {
      setBusy(false);
    }
  }

  const heading = isFirstTime ? "create first admin" : "admin sign in";
  const subheading = isFirstTime
    ? "no admin exists yet — set up the first account."
    : "restricted area — staff only";
  const cta = isFirstTime ? "create admin account" : "sign in";

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-16 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <Card className="w-full max-w-md p-8 border-slate-700 bg-slate-900/80 backdrop-blur" style={{ fontFamily: "Poppins, sans-serif" }}>
        <div className="mb-6">
          
          <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">admin portal</p>
          <h1 className="mt-2 text-2xl font-bold text-slate-50">{heading}</h1>
          <p className="mt-1 text-sm text-slate-400">{subheading}</p>
        </div>

        {mfaFactorId ? (

          <form onSubmit={handleMfaSubmit} className="space-y-4">
            <div className="rounded-md border border-emerald-700/50 bg-emerald-900/20 p-3">
              <p className="text-xs text-emerald-200/90">
                Two-factor authentication required. Open Google Authenticator and enter the 6-digit code for this account.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="mfa" className="text-slate-300">authentication code</Label>
              <Input
                id="mfa"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="\d{6}"
                maxLength={6}
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ""))}
                required
                autoFocus
                className="bg-slate-800 border-slate-700 text-slate-100 text-center tracking-[0.4em] font-mono text-lg"
                placeholder="123456"
              />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Button type="submit" disabled={busy} className="w-full">
              {busy ? "..." : "verify and continue"}
            </Button>
            <button
              type="button"
              onClick={handleMfaCancel}
              className="block w-full text-center text-xs text-slate-400 hover:text-slate-200"
            >
              cancel and sign out
            </button>
          </form>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {isFirstTime && (
              <div className="rounded-md border border-amber-700/50 bg-amber-900/20 p-3">
                <p className="text-xs text-amber-200/90">
                  first-time setup: create a new account or sign in with an existing account to claim admin access.
                </p>
              </div>
            )}

            {isFirstTime && user && (
              <div className="space-y-3 rounded-md border border-slate-700 bg-slate-800/60 p-3">
                <p className="text-xs text-slate-300">
                  signed in as <span className="font-medium text-slate-100">{user.email}</span>
                </p>
                <div className="flex flex-col gap-2">
                  <Button type="button" onClick={handleClaimCurrentAccount} disabled={busy} className="w-full">
                    {busy ? "..." : "claim admin with current account"}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={async () => {
                      await signOut();
                      setInfo("signed out — now create or sign in with another account.");
                    }}
                    disabled={busy}
                    className="w-full"
                  >
                    use another account
                  </Button>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-300">email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" className="bg-slate-800 border-slate-700 text-slate-100" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-300">password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  autoComplete={isFirstTime ? "new-password" : "current-password"}
                  className="bg-slate-800 border-slate-700 text-slate-100 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
                  aria-label={showPassword ? "hide password" : "show password"}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {isFirstTime && <p className="text-xs text-slate-500">minimum 8 characters</p>}
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}
            {info && <p className="text-sm text-emerald-400">{info}</p>}

            <Button type="submit" disabled={busy} className="w-full">
              {busy ? "..." : cta}
            </Button>
            {!isFirstTime && (
              <button
                type="button"
                onClick={() => setResetOpen(true)}
                className="w-full text-xs text-slate-400 hover:text-slate-200 underline"
              >
                forgot password?
              </button>
            )}
            <PasswordResetDialog open={resetOpen} onOpenChange={setResetOpen} defaultEmail={email} />
          </form>
        )}

      </Card>
    </div>
  );
}

export const Route = createFileRoute("/admin/login")({
  validateSearch: (s: Record<string, unknown> & SearchSchemaInput) => ({
    error: typeof s.error === "string" ? s.error : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Admin sign in" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminLoginPage,
});
